import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Database, MessageSquare, Mail, Globe, Key, Shield } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    facturama_user: "",
    facturama_password: "",
    facturama_sandbox: true,
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_whatsapp_number: "",
    sendgrid_api_key: "",
    cdn_enabled: false,
    cdn_base_url: ""
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/admin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("cia_token");
      
      let dataToSave = {};
      switch (section) {
        case "facturama":
          dataToSave = {
            facturama_user: config.facturama_user,
            facturama_password: config.facturama_password !== "********" ? config.facturama_password : undefined,
            facturama_sandbox: config.facturama_sandbox
          };
          break;
        case "twilio":
          dataToSave = {
            twilio_account_sid: config.twilio_account_sid,
            twilio_auth_token: config.twilio_auth_token !== "********" ? config.twilio_auth_token : undefined,
            twilio_whatsapp_number: config.twilio_whatsapp_number
          };
          break;
        case "email":
          dataToSave = {
            sendgrid_api_key: !config.sendgrid_api_key.startsWith("sk-****") ? config.sendgrid_api_key : undefined
          };
          break;
        case "cdn":
          dataToSave = {
            cdn_enabled: config.cdn_enabled,
            cdn_base_url: config.cdn_base_url
          };
          break;
        default:
          return;
      }

      // Remove undefined values
      Object.keys(dataToSave).forEach(key => dataToSave[key] === undefined && delete dataToSave[key]);

      if (Object.keys(dataToSave).length === 0) {
        toast.info("No hay cambios para guardar");
        setSaving(false);
        return;
      }

      await axios.put(`${API}/admin/config`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Configuración guardada");
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-config-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Configuración del Sistema</h1>
        <p className="page-subtitle">Administra las integraciones y servicios externos</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Facturama */}
        <Card className="cia-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Facturama (PAC)</CardTitle>
                <CardDescription>Conexión para timbrado de CFDI</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Usuario Facturama</Label>
              <Input
                value={config.facturama_user}
                onChange={(e) => setConfig({ ...config, facturama_user: e.target.value })}
                placeholder="usuario@empresa.com"
                className="cia-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña/API Key</Label>
              <Input
                type="password"
                value={config.facturama_password}
                onChange={(e) => setConfig({ ...config, facturama_password: e.target.value })}
                placeholder="••••••••"
                className="cia-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Modo Sandbox</p>
                <p className="text-xs text-slate-500">Activar para pruebas</p>
              </div>
              <Switch
                checked={config.facturama_sandbox}
                onCheckedChange={(checked) => setConfig({ ...config, facturama_sandbox: checked })}
              />
            </div>
            <Button onClick={() => handleSave("facturama")} disabled={saving} className="w-full cia-btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Facturama
            </Button>
          </CardContent>
        </Card>

        {/* Twilio WhatsApp */}
        <Card className="cia-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-base">Twilio WhatsApp</CardTitle>
                <CardDescription>Integración con WhatsApp Business</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Account SID</Label>
              <Input
                value={config.twilio_account_sid}
                onChange={(e) => setConfig({ ...config, twilio_account_sid: e.target.value })}
                placeholder="ACxxxxxxxxxxxxxxx"
                className="cia-input font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Auth Token</Label>
              <Input
                type="password"
                value={config.twilio_auth_token}
                onChange={(e) => setConfig({ ...config, twilio_auth_token: e.target.value })}
                placeholder="••••••••"
                className="cia-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Número WhatsApp</Label>
              <Input
                value={config.twilio_whatsapp_number}
                onChange={(e) => setConfig({ ...config, twilio_whatsapp_number: e.target.value })}
                placeholder="+14155238886"
                className="cia-input"
              />
            </div>
            <Button onClick={() => handleSave("twilio")} disabled={saving} className="w-full cia-btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Twilio
            </Button>
          </CardContent>
        </Card>

        {/* SendGrid Email */}
        <Card className="cia-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base">SendGrid Email</CardTitle>
                <CardDescription>Servicio de correo electrónico</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input
                type="password"
                value={config.sendgrid_api_key}
                onChange={(e) => setConfig({ ...config, sendgrid_api_key: e.target.value })}
                placeholder="SG.xxxxxxxx"
                className="cia-input font-mono text-sm"
              />
            </div>
            <Button onClick={() => handleSave("email")} disabled={saving} className="w-full cia-btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar SendGrid
            </Button>
          </CardContent>
        </Card>

        {/* CDN */}
        <Card className="cia-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base">CDN / Storage</CardTitle>
                <CardDescription>Almacenamiento de archivos</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Habilitar CDN</p>
                <p className="text-xs text-slate-500">Servir archivos desde CDN</p>
              </div>
              <Switch
                checked={config.cdn_enabled}
                onCheckedChange={(checked) => setConfig({ ...config, cdn_enabled: checked })}
              />
            </div>
            {config.cdn_enabled && (
              <div className="space-y-2">
                <Label>URL Base del CDN</Label>
                <Input
                  value={config.cdn_base_url}
                  onChange={(e) => setConfig({ ...config, cdn_base_url: e.target.value })}
                  placeholder="https://cdn.tudominio.com"
                  className="cia-input"
                />
              </div>
            )}
            <Button onClick={() => handleSave("cdn")} disabled={saving} className="w-full cia-btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar CDN
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="cia-card border-yellow-200 bg-yellow-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Importante</p>
              <p className="text-sm text-yellow-700">
                Las credenciales se almacenan de forma segura y encriptada. 
                Los cambios en Facturama afectan el timbrado de todas las empresas.
                Asegúrate de probar en modo Sandbox antes de producción.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminConfigPage;
